import { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  // Reference for the printable component
  const receiptRef = useRef();
  // Add state for save status indicator
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'

  const [invoiceData, setInvoiceData] = useState({
    companyLogo: '',
    companyName: '',
    companyAddress: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    customerName: '',
    items: [{ 
      id: Date.now(), 
      name: '', 
      pricePerItem: '', 
      qty: 1 
    }],
    taxPercentage: 11
  });

  // Load data from localStorage on component mount
  useEffect(() => {
    try {
      const savedInvoiceData = localStorage.getItem('thermalInvoiceData');
      if (savedInvoiceData) {
        const parsedData = JSON.parse(savedInvoiceData);
        console.log('Loaded data from localStorage:', parsedData);
        setInvoiceData(parsedData);
      } else {
        console.log('No saved data found in localStorage');
      }
    } catch (error) {
      console.error('Error loading data from localStorage:', error);
    }
  }, []);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    try {
      console.log('Saving data to localStorage:', invoiceData);
      setSaveStatus('saving');
      
      // Use setTimeout to simulate the saving process and provide visual feedback
      const saveTimer = setTimeout(() => {
        localStorage.setItem('thermalInvoiceData', JSON.stringify(invoiceData));
        setSaveStatus('saved');
      }, 300);
      
      return () => clearTimeout(saveTimer);
    } catch (error) {
      console.error('Error saving data to localStorage:', error);
      setSaveStatus('error');
      // Try to show an error notification to the user
      alert('Failed to save your data. Your browser might have localStorage disabled.');
    }
  }, [invoiceData]);

  // Detect if localStorage is available
  useEffect(() => {
    const testStorageAvailability = () => {
      const test = 'test';
      try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    };
    
    if (!testStorageAvailability()) {
      console.error('localStorage is not available. Data persistence will not work.');
      alert('Your browser does not support or has disabled local storage. Changes will not be saved between page refreshes.');
    }
  }, []);

  // Add beforeunload event listener to warn users before refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const message = "You have unsaved changes that will be lost if you refresh or leave this page.";
      e.returnValue = message; // Standard for most browsers
      return message; // For some older browsers
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData({
      ...invoiceData,
      [name]: value
    });
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setInvoiceData({
          ...invoiceData,
          companyLogo: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle item changes
  const handleItemChange = (id, field, value) => {
    setInvoiceData({
      ...invoiceData,
      items: invoiceData.items.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  };

  // Add new item
  const addItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [...invoiceData.items, { id: Date.now(), name: '', pricePerItem: '', qty: 1 }]
    });
  };

  // Remove item
  const removeItem = (id) => {
    setInvoiceData({
      ...invoiceData,
      items: invoiceData.items.filter(item => item.id !== id)
    });
  };

  // Calculate subtotal
  const subtotal = invoiceData.items.reduce((sum, item) => {
    const price = parseFloat(item.pricePerItem) || 0;
    const qty = parseFloat(item.qty) || 1;
    return sum + (price * qty);
  }, 0);

  // Calculate tax
  const taxAmount = (subtotal * (invoiceData.taxPercentage / 100)).toFixed(2);
  
  // Calculate grand total
  const grandTotal = (subtotal + parseFloat(taxAmount)).toFixed(2);

  // Format number with thousand separators
  const formatNumber = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  // Reset form data
  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all invoice data?')) {
      setInvoiceData({
        companyLogo: '',
        companyName: '',
        companyAddress: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        customerName: '',
        items: [{ id: Date.now(), name: '', pricePerItem: '', qty: 1 }],
        taxPercentage: 11
      });
      localStorage.removeItem('thermalInvoiceData');
    }
  };

  return (
    <div className="App">
      <div className="container">
        <h1>Thermal Invoice Generator</h1>
        {/* Add save status indicator */}
        <div className={`save-status ${saveStatus}`}>
          {saveStatus === 'saved' && '✓ All changes saved'}
          {saveStatus === 'saving' && '⏳ Saving...'}
          {saveStatus === 'error' && '⚠️ Failed to save changes'}
        </div>
        <div className="invoice-generator">
          <div className="invoice-form">
            <h2>Invoice Details</h2>
            <div className="form-section">
              <h3>Company Information</h3>
              <div className="form-group">
                <label>Company Logo:</label>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleLogoUpload}
                />
                {invoiceData.companyLogo && (
                  <div className="logo-preview">
                    <img src={invoiceData.companyLogo} alt="Company Logo" width="100" />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Company Name:</label>
                <input
                  type="text"
                  name="companyName"
                  value={invoiceData.companyName}
                  onChange={handleInputChange}
                  placeholder="Your Company Name"
                />
              </div>
              <div className="form-group">
                <label>Company Address:</label>
                <textarea
                  name="companyAddress"
                  value={invoiceData.companyAddress}
                  onChange={handleInputChange}
                  placeholder="Your Company Address"
                ></textarea>
              </div>
            </div>

            <div className="form-section">
              <h3>Invoice Information</h3>
              <div className="form-group">
                <label>Invoice Number:</label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={invoiceData.invoiceNumber}
                  onChange={handleInputChange}
                  placeholder="INV-0001"
                />
              </div>
              <div className="form-group">
                <label>Invoice Date:</label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={invoiceData.invoiceDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Customer Name:</label>
                <input
                  type="text"
                  name="customerName"
                  value={invoiceData.customerName}
                  onChange={handleInputChange}
                  placeholder="Customer Name"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Items</h3>
              {invoiceData.items.map((item, index) => (
                <div key={item.id} className="item-row">
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Item name"
                      value={item.name}
                      onChange={(e) => handleItemChange(item.id, 'name', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="number"
                      placeholder="Qty"
                      min="1"
                      value={item.qty}
                      onChange={(e) => handleItemChange(item.id, 'qty', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="number"
                      placeholder="Price per item"
                      value={item.pricePerItem}
                      onChange={(e) => handleItemChange(item.id, 'pricePerItem', e.target.value)}
                    />
                  </div>
                  <button 
                    type="button" 
                    className="remove-btn"
                    onClick={() => removeItem(item.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className="add-btn" onClick={addItem}>
                Add Item
              </button>
            </div>

            <div className="form-section">
              <h3>Tax Settings</h3>
              <div className="form-group">
                <label>Tax Percentage:</label>
                <input
                  type="number"
                  name="taxPercentage"
                  value={invoiceData.taxPercentage}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button onClick={handleReset} className="reset-btn">
                Reset Form
              </button>
            </div>
          </div>

          <div className="invoice-preview">
            <h2>Invoice Preview</h2>
            <div className="thermal-receipt" ref={receiptRef}>
              {invoiceData.companyLogo && (
                <div className="receipt-logo">
                  <img src={invoiceData.companyLogo} alt="Company Logo" />
                </div>
              )}
              <div className="receipt-header">
                <h2>{invoiceData.companyName || "Company Name"}</h2>
                <p>{invoiceData.companyAddress || "Company Address"}</p>
                <div className="receipt-divider">================================</div>
                <p>Invoice #: {invoiceData.invoiceNumber || "INV-0001"}</p>
                <p>Date: {invoiceData.invoiceDate || new Date().toLocaleDateString()}</p>
                <p>Customer: {invoiceData.customerName || "Customer"}</p>
                <div className="receipt-divider">================================</div>
              </div>
              
              <div className="receipt-items">
                <table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.items.map((item, index) => {
                      const itemTotal = (parseFloat(item.pricePerItem) || 0) * (parseFloat(item.qty) || 1);
                      return (
                        <tr key={item.id}>
                          <td>{item.name || `Item ${index + 1}`}</td>
                          <td>{item.qty || 1}</td>
                          <td>{item.pricePerItem ? `Rp ${formatNumber(parseFloat(item.pricePerItem).toFixed(0))}` : 'Rp 0'}</td>
                          <td>{`Rp ${formatNumber(itemTotal.toFixed(0))}`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="receipt-divider">--------------------------------</div>
              </div>
              
              <div className="receipt-summary">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>Rp {formatNumber(subtotal.toFixed(0))}</span>
                </div>
                <div className="summary-row">
                  <span>Tax ({invoiceData.taxPercentage}%):</span>
                  <span>Rp {formatNumber(parseFloat(taxAmount).toFixed(0))}</span>
                </div>
                <div className="receipt-divider">--------------------------------</div>
                <div className="summary-row total">
                  <span>TOTAL:</span>
                  <span>Rp {formatNumber(parseFloat(grandTotal).toFixed(0))}</span>
                </div>
              </div>
              
              <div className="receipt-footer">
                <div className="receipt-divider">================================</div>
                <p>Thank you for your business!</p>
                <p>{new Date().toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
